import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Copy, ExternalLink, Users, TrendingUp, DollarSign, LogOut, CheckCircle, XCircle, Link, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AffiliateDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLinks, setShowLinks] = useState(false);
  const [showReferrals, setShowReferrals] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/affiliates/me/stats`, { credentials: "include" });
      if (res.status === 401) { navigate("/affiliate/login", { replace: true }); return; }
      if (res.ok) setData(await res.json());
    } catch { toast.error("Failed to load dashboard"); }
    finally { setLoading(false); }
  };

  const handleLogout = async () => {
    await fetch(`${API}/affiliates/auth/logout`, { method: "POST", credentials: "include" });
    navigate("/affiliate/login", { replace: true });
  };

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#070B14] flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500" />
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-[#070B14] flex items-center justify-center text-white">
      <p>Unable to load data. Please try again.</p>
    </div>
  );

  const { affiliate, stats, payouts, referrals = [], program_links = [], referral_link } = data;

  return (
    <div className="min-h-screen bg-[#070B14] text-white" data-testid="affiliate-dashboard">
      {/* Header */}
      <div className="border-b border-[#1E293B]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-black tracking-wider"><span className="text-blue-500">PALADIN</span> AFFILIATES</h1>
            <p className="text-slate-500 text-xs mt-0.5">Welcome, {affiliate.name}</p>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors" data-testid="affiliate-logout-btn">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* Your Info */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-[#0f172a] border border-[#1E293B] rounded-2xl p-6">
          <div className="flex items-center gap-6 flex-wrap text-sm text-slate-400">
            <span>Coupon: <span className="text-white font-mono bg-[#070B14] px-2 py-0.5 rounded" data-testid="affiliate-coupon-code">{affiliate.coupon_code}</span></span>
            <span>Discount: <span className="text-green-400">{affiliate.discount_display} forever</span></span>
            <span>Commission: <span className="text-amber-400">{affiliate.commission_value}% recurring</span></span>
          </div>
        </motion.div>

        {/* Program Links */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-[#0f172a] border border-[#1E293B] rounded-2xl overflow-hidden">
          <button onClick={() => setShowLinks(!showLinks)} className="w-full p-6 flex items-center justify-between text-left">
            <div className="flex items-center gap-3">
              <Link className="w-5 h-5 text-blue-400" />
              <div>
                <h2 className="font-semibold text-lg">Your Referral Links</h2>
                <p className="text-slate-500 text-xs mt-0.5">Copy links for each program — your ref & coupon are built in</p>
              </div>
            </div>
            {showLinks ? <ChevronUp className="w-5 h-5 text-slate-500" /> : <ChevronDown className="w-5 h-5 text-slate-500" />}
          </button>
          {showLinks && (
            <div className="px-6 pb-6 space-y-3">
              {program_links.map((link, i) => (
                <div key={i} className="flex items-center gap-3 bg-[#070B14] rounded-xl p-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-400 mb-0.5">{link.name}</p>
                    <code className="text-blue-400 text-xs truncate block">{link.url}</code>
                  </div>
                  <button onClick={() => copyText(link.url, link.name)} className="flex items-center gap-1.5 bg-blue-500/20 text-blue-400 px-3 py-1.5 rounded-lg hover:bg-blue-500/30 transition-colors text-xs font-medium flex-shrink-0" data-testid={`copy-link-${i}`}>
                    <Copy className="w-3.5 h-3.5" /> Copy
                  </button>
                </div>
              ))}
              <p className="text-[11px] text-slate-600 mt-2">Tip: Add <code className="text-slate-500">&code=YOURCODE</code> to any link to auto-apply a discount at checkout.</p>
            </div>
          )}
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Total Referrals", value: stats.total_referrals, icon: Users, color: "text-blue-400" },
            { label: "Active", value: stats.active_referrals, icon: CheckCircle, color: "text-green-400" },
            { label: "Churned", value: stats.churned_referrals, icon: XCircle, color: "text-red-400" },
            { label: "Revenue Generated", value: `$${stats.total_revenue.toFixed(2)}`, icon: TrendingUp, color: "text-blue-400" },
          ].map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-[#0f172a] border border-[#1E293B] rounded-xl p-5" data-testid={`affiliate-stat-${i}`}>
              <s.icon className={`w-6 h-6 ${s.color} mb-2`} />
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-slate-500 text-xs mt-0.5">{s.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Earnings Summary */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-[#0f172a] border border-[#1E293B] rounded-2xl p-6">
          <h2 className="font-semibold text-lg mb-4">Earnings</h2>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <p className="text-slate-500 text-xs">Total Commission Earned</p>
              <p className="text-3xl font-bold text-green-400" data-testid="affiliate-total-commission">${stats.total_commission.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-slate-500 text-xs">Total Paid Out</p>
              <p className="text-3xl font-bold text-white" data-testid="affiliate-total-paid">${stats.total_paid.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-slate-500 text-xs">Outstanding Balance</p>
              <p className="text-3xl font-bold text-amber-400" data-testid="affiliate-commission-owed">${stats.commission_owed.toFixed(2)}</p>
            </div>
          </div>
        </motion.div>

        {/* Referral List */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="bg-[#0f172a] border border-[#1E293B] rounded-2xl overflow-hidden">
          <button onClick={() => setShowReferrals(!showReferrals)} className="w-full p-6 flex items-center justify-between text-left">
            <div>
              <h2 className="font-semibold text-lg">Your Referrals</h2>
              <p className="text-slate-500 text-xs mt-0.5">{stats.total_referrals} people signed up through your links</p>
            </div>
            {showReferrals ? <ChevronUp className="w-5 h-5 text-slate-500" /> : <ChevronDown className="w-5 h-5 text-slate-500" />}
          </button>
          {showReferrals && (
            <div className="px-6 pb-6">
              {referrals.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-10 h-10 text-slate-700 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">No referrals yet. Share your links to get started!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {referrals.map((r, i) => (
                    <div key={i} className="flex items-center justify-between py-3 px-4 bg-[#070B14] rounded-lg" data-testid={`referral-row-${i}`}>
                      <div>
                        <p className="text-sm font-medium">{r.name || r.email || "Unknown"}</p>
                        <p className="text-xs text-slate-500">{r.email}</p>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs px-2 py-0.5 rounded ${r.subscription_status === "active" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                          {r.subscription_status === "active" ? "Active" : "Inactive"}
                        </span>
                        <p className="text-xs text-slate-500 mt-1 capitalize">{r.subscription_tier || "—"}</p>
                        <p className="text-xs text-slate-600">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Payout History */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-[#0f172a] border border-[#1E293B] rounded-2xl p-6">
          <h2 className="font-semibold text-lg mb-4">Payout History</h2>
          {payouts.length === 0 ? (
            <div className="text-center py-8">
              <DollarSign className="w-10 h-10 text-slate-700 mx-auto mb-2" />
              <p className="text-slate-500 text-sm">No payouts yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {payouts.map((p, i) => (
                <div key={p.payout_id || i} className="flex items-center justify-between py-3 px-4 bg-[#070B14] rounded-lg" data-testid={`payout-row-${i}`}>
                  <div>
                    <p className="text-sm font-medium">${p.amount.toFixed(2)}</p>
                    <p className="text-xs text-slate-500">{p.notes || "Payout"}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">{p.status}</span>
                    <p className="text-xs text-slate-500 mt-1">{new Date(p.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
